import type { UnresolvedRecord } from '../interfaces'
import { TemplateActionNames } from '../consts/defaultConfigValues/defaultConstants'
import { isObject } from './validTypes'

/**
 * Args that hold actions of their own, which no pass may resolve before
 * those actions run.
 *
 * `processData` recurses into every nested object and array, and an
 * authored action goes through it three times before it runs: the config
 * pass (`processConfig.getComponentsProps`, `initial: true`), the render pass
 * (`useCinnamonProps`), and the click (`createFunction`). A handler resolved
 * by any of them binds its `{{this.*}}` to something other than the step's
 * answer, and an `||` or `??` expression is the worst case: the config pass
 * settles `{{this.message}} || {{texts.fallback}}` on the fallback, because
 * `texts` is a constant source and `this` resolves to nothing, so the
 * expression is gone before the step answers (PR #843 review). So every pass
 * leaves these args raw, and `runCinnamonActions` resolves them when they
 * run, with the step's answer and the store together.
 *
 * Keyed by action name, so no other action's args change meaning: an
 * existing action with an arg that happens to be called `onError` keeps
 * resolving it in every pass. A `Map` rather than an object literal so an
 * action named `constructor` finds nothing on the prototype chain.
 *
 * Known limit: an arg that arrives through a `template` key is merged by
 * `processData` itself, so it is resolved before this split can see it.
 */
export const DEFERRED_ACTION_ARGS: ReadonlyMap<string, readonly string[]> =
  new Map([
    [
      TemplateActionNames.WORKFLOW_RUN_STEP,
      ['onOutcome', 'onTimeout', 'onError'],
    ],
  ])

/**
 * The deferred arg names of an authored action object (`{ name, args }`),
 * or `undefined` when it is not one whose args defer anything. Anything with
 * a string `name` in `DEFERRED_ACTION_ARGS` and an object `args` counts,
 * wherever it sits: under `cinnamonActions`, in a place-order queue, or in
 * another handler.
 */
export const deferredArgsOfAction = (
  record: UnresolvedRecord,
): readonly string[] | undefined => {
  const { name, args } = record
  if (typeof name !== 'string' || !isObject(args)) {
    return undefined
  }
  return DEFERRED_ACTION_ARGS.get(name)
}

interface ISplitDeferredArgs {
  /** The args every pass resolves. */
  eager: UnresolvedRecord
  /** The args left raw until their actions run. */
  deferred: UnresolvedRecord
}

/** Splits an action's args into the ones to resolve now and the ones to keep raw. */
export const splitDeferredArgs = (
  args: UnresolvedRecord,
  deferredNames: readonly string[],
): ISplitDeferredArgs => {
  const eager: UnresolvedRecord = {}
  const deferred: UnresolvedRecord = {}
  Object.keys(args).forEach((key) => {
    const target = deferredNames.includes(key) ? deferred : eager
    target[key] = args[key]
  })
  return { eager, deferred }
}
